import type { PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';

import { useSidepanel } from '../../../../../../../../../shared/contexts/Sidepanel.provider';
import type { ResponseProjectBlock } from '../../../../../../../../../shared/entities/ResponseProjectBlock';
import { useResponseProjectDetailContext } from '../../../context/responseProjectDetailContext';
import { useCreateResponseProjectThread } from '../hooks/projectAssistant.hook';
import { ResponseProjectAssistant } from '../responseProjectAssistant';

type assitantState = ResponseProjectBlock;

interface ResponseProjectAssistantContextType {
  onOpenAssistant: (data: assitantState) => void;
}

const ResponseProjectAssistantContext = createContext<
  ResponseProjectAssistantContextType | undefined
>(undefined);

export function ResponseProjectAssistantProvider({
  children,
}: PropsWithChildren) {
  const { togglePanel: _togglePanel, closePanel } = useSidepanel();
  const { onUpdateBlockContent } = useResponseProjectDetailContext();
  const { mutateAsync: createThread } = useCreateResponseProjectThread();
  const onOpenAssistant = async (data: assitantState) => {
    /** if the block has no threadId, create a new thread */
    if (!data.threadId) {
      data.updateThreadId(
        await createThread({
          blockId: data.id,
        }).then(thread => thread.id),
      );
    }

    /** if after creating a thread, the block has no threadId, return */
    if (!data.threadId) {
      return;
    }

    _togglePanel({
      content: (
        <ResponseProjectAssistant
          block={data}
          closePanel={closePanel}
          onUpdateBlockContent={onUpdateBlockContent}
          threadId={data.threadId}
        />
      ),
    });
  };

  return (
    <ResponseProjectAssistantContext.Provider
      value={{
        onOpenAssistant,
      }}
    >
      {children}
    </ResponseProjectAssistantContext.Provider>
  );
}

export const useResponseProjectAssistant = () => {
  const context = useContext(ResponseProjectAssistantContext);
  if (!context) {
    throw new Error(
      'useResponseProjectAssistant must be used within a ResponseProjectAssistantProvider',
    );
  }
  return context;
};
